import { Injectable, inject, DestroyRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { datadogRum } from '@datadog/browser-rum';
import { environment } from '../../../environments/environment';
import { UserContextService } from './user-context.service';
import { UserContext } from '@shared/models/user-context.model';

interface DatadogRumConfig {
    enabled?: boolean;
    applicationId?: string;
    clientToken?: string;
    site?: string;
    service?: string;
    env?: string;
    version?: string;
    sessionSampleRate?: number;
    sessionReplaySampleRate?: number;
}

/**
 * Datadog Real User Monitoring (RUM) service.
 * Initializes RUM tracking, sets user/tenant context, and tracks route changes.
 *
 * RUM is only enabled when `environment.datadogRum.enabled` is true and
 * both `applicationId` and `clientToken` are configured.
 */
@Injectable({
    providedIn: 'root'
})
export class DatadogRumService {
    private readonly _router = inject(Router);
    private readonly _userContextService = inject(UserContextService);
    private readonly _destroyRef = inject(DestroyRef);
    private _initialized = false;

    /**
     * Initialize Datadog RUM. Call once during app bootstrap.
     * Safe to call multiple times — subsequent calls are no-ops.
     */
    init(): void {
        if (this._initialized) {
            return;
        }

        const config = (environment as Record<string, unknown>)['datadogRum'] as DatadogRumConfig | undefined;
        if (!config?.enabled || !config?.applicationId || !config?.clientToken) {
            return;
        }

        datadogRum.init({
            applicationId: config.applicationId,
            clientToken: config.clientToken,
            site: config.site || 'datadoghq.eu',
            service: config.service || 'cp',
            env: config.env || 'unknown',
            version: config.version || '0.0.0',
            sessionSampleRate: config.sessionSampleRate ?? 100,
            sessionReplaySampleRate: config.sessionReplaySampleRate ?? 20,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            defaultPrivacyLevel: 'mask-user-input',
            allowedTracingUrls: [environment.apiConfig.uri],
        });

        this._initialized = true;
        if ((config.sessionReplaySampleRate ?? 0) > 0) {
            datadogRum.startSessionReplayRecording();
        }
        this.trackRouteChanges();
        this.trackUserContext();
    }

    /**
     * Track Angular route changes as RUM views.
     */
    private trackRouteChanges(): void {
        this._router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((event: NavigationEnd) => {
            datadogRum.startView({ name: event.urlAfterRedirects });
        });
    }

    /**
     * Set Datadog user/tenant context when user context changes.
     * Only sends userId — no PII (email, name) per Rule Zero.
     */
    private trackUserContext(): void {
        this._userContextService.userContext$.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((context: UserContext | null) => {
            if (context) {
                datadogRum.setUser({ id: context.userId });
                datadogRum.setGlobalContextProperty('tenant', {
                    tenantId: context.tenantId,
                    siteKey: context.siteId,
                    siteInstanceKey: context.siteInstanceKey,
                });
            } else {
                datadogRum.clearUser();
                datadogRum.removeGlobalContextProperty('tenant');
            }
        });
    }
}
